import {
  BookOpen,
  ChevronRight,
  Cloud,
  CloudOff,
  Command,
  Crown,
  LogOut,
  Menu,
  Search,
  Ticket,
  User,
} from 'lucide-react';
import { useContext, useEffect, useMemo, useState, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/context';
import { useTemplateConfig } from '../configContext';
import { WorkspaceContext } from '../workspace/context';
import { defaultBreadcrumbResolver, type BreadcrumbResolver } from '../lib/breadcrumb';
import BrandMark from './BrandMark';
import {
  useSubscriptionTier,
  useEntitledLanguagePairs,
  useDayPassCreditCount,
} from '@/platform/stores/useAuthStore';
import { useT } from '@/i18n';

interface HeaderProps {
  onOpenMenu: () => void;
  menuButtonRef: RefObject<HTMLButtonElement | null>;
  menuOpen: boolean;
  /** Optional override for the breadcrumb derivation. Default resolver title-cases path segments. */
  breadcrumbResolver?: BreadcrumbResolver;
  /** Resolved dynamic-segment label (e.g., a project name). Passed to the resolver. */
  resolvedSegmentName?: string | null;
}

function formatRoleLabel(role?: string | null) {
  if (!role) return 'Account';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function SubscriptionBadge() {
  const { t } = useT();
  const tier = useSubscriptionTier();
  if (tier === 'FREE') return null;
  return (
    <Link
      to="/subscription"
      title={t('shell.subscription', { defaultValue: `Subscription: ${tier}`, tier })}
      className="hidden items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white md:inline-flex"
    >
      <Crown className="h-3 w-3" />
      {tier === 'PREMIUM' ? 'Premium' : tier === 'PROFESSIONAL' ? 'Pro' : tier}
    </Link>
  );
}

function formatRemaining(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
}

function DayPassCountdownBadge() {
  const { t } = useT();
  const pairs = useEntitledLanguagePairs();
  const creditCount = useDayPassCreditCount();
  const [now, setNow] = useState(() => Date.now());

  const active = useMemo(() => pairs.find((p) => p.source === 'day_pass_active'), [pairs]);

  useEffect(() => {
    if (!active?.expiresAt) return;
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(tick);
  }, [active?.expiresAt]);

  if (active?.expiresAt) {
    const remaining = new Date(active.expiresAt).getTime() - now;
    if (remaining > 0) {
      return (
        <Link
          to="/subscription"
          title={`Day Pass active for ${active.native.toUpperCase()} ↔ ${active.target.toUpperCase()}`}
          className="hidden items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white md:inline-flex"
        >
          <Ticket className="h-3 w-3" />
          {active.native.toUpperCase()}↔{active.target.toUpperCase()} · {formatRemaining(remaining)}
        </Link>
      );
    }
  }

  if (creditCount > 0) {
    return (
      <Link
        to="/subscription"
        title={t('shell.dayPassCredits', {
          defaultValue: '{{count}} unused Day Pass credits',
          count: creditCount,
        })}
        className="hidden items-center gap-1 rounded-full border border-white/15 bg-white/6 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/82 md:inline-flex"
      >
        <Ticket className="h-3 w-3" />
        {t('shell.creditCount', { defaultValue: '{{count}} credits', count: creditCount })}
      </Link>
    );
  }

  return null;
}

export default function Header({
  onOpenMenu,
  menuButtonRef,
  menuOpen,
  breadcrumbResolver,
  resolvedSegmentName,
}: HeaderProps) {
  const { t } = useT();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useTemplateConfig();
  const tenancyEnabled = config.tenancy === 'enabled';
  // Always read the context unconditionally to obey rules-of-hooks; only USE
  // the value when tenancy is enabled. Returns null when no provider is mounted.
  const workspaceCtx = useContext(WorkspaceContext);
  const workspace = tenancyEnabled ? workspaceCtx : null;
  const [isOnline, setIsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const resolver = breadcrumbResolver ?? defaultBreadcrumbResolver;
  const breadcrumb = useMemo(
    () => resolver(location.pathname, { resolvedSegmentName }),
    [resolver, location.pathname, resolvedSegmentName]
  );

  const workspaceName = workspace?.activeOrganization?.name ?? 'Admin Workspace';
  const userInitials =
    (user?.displayName ?? user?.email ?? 'CE')
      .split(/[ @]/)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || 'CE';

  const brand = config.brand;
  const appEnv = (config.appEnv ?? 'development').trim().toLowerCase();
  const [searchQuery, setSearchQuery] = useState('');
  const searchResults = config.navigation
    .flatMap((item) => [item, ...(item.children ?? [])])
    .filter(
      (item) => item.path && item.label.toLowerCase().includes(searchQuery.trim().toLowerCase())
    )
    .slice(0, 6);

  const submitSearch = () => {
    const firstResult = searchResults[0];
    if (firstResult?.path) {
      void navigate(firstResult.path);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-gradient-to-r from-[var(--ce-color-ink)] to-[#18386f] text-white">
      {/* ─── Row 1: chrome ────────────────────────────────────────────── */}
      <div className="flex h-14 items-center gap-3 px-3 sm:px-5 lg:px-6">
        <button
          type="button"
          onClick={onOpenMenu}
          ref={menuButtonRef}
          aria-label={t('shell.openNavigation', { defaultValue: 'Open navigation' })}
          aria-controls="mobile-navigation-drawer"
          aria-expanded={menuOpen}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-white/10 lg:hidden"
        >
          <Menu className="h-4 w-4" />
        </button>

        <Link to={config.routes.dashboard} className="flex items-center gap-2.5">
          <BrandMark
            brand={brand}
            className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-white/12 bg-white/6 p-0.5"
            fallbackClassName="rounded-lg border-0 bg-transparent p-0 text-sm font-bold text-white"
            style={{ background: brand.accentColor ?? 'var(--ce-color-accent)' }}
          />
          <div className="hidden flex-col leading-tight md:flex">
            <span className="text-sm font-semibold">{brand.name}</span>
            {brand.versionLabel ? (
              <span className="text-[10px] uppercase tracking-[0.16em] text-white/55">
                {brand.versionLabel}
              </span>
            ) : null}
          </div>
        </Link>

        {tenancyEnabled ? (
          <>
            <span className="hidden h-7 w-px bg-white/12 md:inline-block" />
            <div className="hidden min-w-0 md:block">
              <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-white/55">
                {t('shell.tenant', { defaultValue: 'tenant' })}
              </div>
              <div className="truncate text-sm font-medium">{workspaceName}</div>
            </div>
          </>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          <span
            className={`hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium md:inline-flex ${
              isOnline
                ? 'border-[rgba(39,174,96,0.32)] bg-[rgba(39,174,96,0.12)] text-white'
                : 'border-[rgba(211,31,16,0.32)] bg-[rgba(211,31,16,0.12)] text-white'
            }`}
            title={
              isOnline
                ? t('shell.online', { defaultValue: 'Online' })
                : t('shell.offline', { defaultValue: 'Offline' })
            }
          >
            {isOnline ? (
              <Cloud className="h-3 w-3 text-[#b8ffd1]" />
            ) : (
              <CloudOff className="h-3 w-3 text-[#ffd2d2]" />
            )}
            {appEnv}
          </span>
          {user?.role ? (
            <span className="hidden rounded-full border border-white/15 bg-white/8 px-2.5 py-1 text-[11px] uppercase tracking-[0.08em] text-white/82 md:inline">
              {formatRoleLabel(user.role)}
            </span>
          ) : null}
          <SubscriptionBadge />
          <DayPassCountdownBadge />

          <a
            href="https://help.whatimean.app"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden h-9 w-9 items-center justify-center rounded-md text-white/78 transition-colors hover:bg-white/10 hover:text-white md:inline-flex"
            title={t('shell.help', { defaultValue: 'Help and docs' })}
          >
            <BookOpen className="h-4 w-4" />
          </a>

          <div className="flex items-center gap-2 rounded-full border border-white/12 bg-white/6 py-1 pl-1 pr-2 md:pr-3">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: brand.accentColor ?? 'var(--ce-color-accent)' }}
            >
              {userInitials}
            </span>
            <div className="hidden min-w-0 md:block">
              <div className="truncate text-xs font-semibold text-white">
                {user?.displayName ?? 'User'}
              </div>
              <div className="truncate text-[10px] uppercase tracking-[0.12em] text-white/55">
                {user?.email}
              </div>
            </div>
            {config.routes.account ? (
              <Link
                to={config.routes.account}
                className="hidden h-8 w-8 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white md:inline-flex"
                title={t('shell.account', { defaultValue: 'Account' })}
              >
                <User className="h-4 w-4" />
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => void signOut()}
              className="hidden h-8 w-8 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white md:inline-flex"
              title={t('shell.signOut', { defaultValue: 'Sign out' })}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Mobile-only breadcrumb strip ─────────────────────────────── */}
      <nav
        aria-label={t('shell.breadcrumb', { defaultValue: 'breadcrumb' })}
        className="flex items-center gap-1 overflow-x-auto border-t border-white/8 px-3 py-1.5 text-[11px] lg:hidden"
      >
        {breadcrumb.map((seg, i) => (
          <span key={`m-${seg}-${i}`} className="flex shrink-0 items-center gap-1">
            {i > 0 ? <ChevronRight className="h-3 w-3 text-white/35" /> : null}
            <span
              className={i === breadcrumb.length - 1 ? 'font-medium text-white' : 'text-white/60'}
            >
              {seg}
            </span>
          </span>
        ))}
      </nav>

      {/* ─── Row 2: breadcrumb + search + quick actions ────────────────── */}
      <div className="border-t border-white/8 px-3 py-2.5 sm:px-5">
        <div className="flex items-center gap-3">
          {/* Breadcrumb (desktop only — mobile has its own strip above) */}
          <nav
            aria-label={t('shell.breadcrumb', { defaultValue: 'breadcrumb' })}
            className="hidden min-w-0 flex-1 items-center gap-1 text-xs lg:flex"
          >
            {breadcrumb.map((seg, i) => (
              <span key={`${seg}-${i}`} className="flex min-w-0 items-center gap-1">
                {i > 0 ? <ChevronRight className="h-3 w-3 shrink-0 text-white/35" /> : null}
                <span
                  className={`truncate rounded-md px-2 py-0.5 ${
                    i === breadcrumb.length - 1
                      ? 'bg-white/12 font-medium text-white'
                      : 'text-white/65'
                  }`}
                >
                  {seg}
                </span>
              </span>
            ))}
          </nav>

          {/* Search — fills full width on mobile, fixed width on desktop */}
          <form
            className="relative flex flex-1 items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm transition-colors focus-within:border-white/30 focus-within:bg-white/12 lg:w-72 lg:flex-none"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch();
            }}
          >
            <Search className="h-4 w-4 shrink-0 text-white/55" />
            <input
              type="search"
              placeholder={t('shell.search', { defaultValue: 'Search…' })}
              aria-label={t('shell.globalSearch', { defaultValue: 'Global search' })}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="min-w-0 flex-1 bg-transparent text-white outline-none placeholder:text-white/45"
            />
            <span className="inline-flex shrink-0 items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 font-mono text-[10px] text-white/55">
              <Command className="h-3 w-3" />K
            </span>
            {searchQuery.trim() ? (
              <div
                className="absolute left-0 top-[calc(100%+0.5rem)] z-50 w-full overflow-hidden rounded-xl border border-[var(--ce-color-border)] bg-white py-1 text-[var(--ce-color-ink)] shadow-xl"
                role="listbox"
                aria-label={t('shell.searchResults', { defaultValue: 'Search results' })}
              >
                {searchResults.length ? (
                  searchResults.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      role="option"
                      aria-selected="false"
                      className="flex w-full px-3 py-2 text-left text-sm hover:bg-[var(--ce-color-accent-soft)]"
                      onClick={() => {
                        if (result.path) void navigate(result.path);
                        setSearchQuery('');
                      }}
                    >
                      {result.label}
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-[var(--ce-color-text-subtle)]">
                    {t('shell.noMatchingPages', { defaultValue: 'No matching pages' })}
                  </p>
                )}
              </div>
            ) : null}
          </form>

          {/* Jump to (max 3) */}
          {config.quickActions.length > 0 ? (
            <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
              <span className="text-[10.5px] uppercase tracking-[0.1em] text-white/45">
                {t('shell.jumpTo', { defaultValue: 'Jump to:' })}
              </span>
              {config.quickActions.slice(0, 3).map((q) => (
                <Link
                  key={`${q.to}-${q.label}`}
                  to={q.to}
                  className="rounded-full border border-white/12 bg-white/6 px-2.5 py-0.5 text-[11px] text-white/85 transition-colors hover:bg-white/12 hover:text-white"
                >
                  {q.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
